import { useCallback } from 'react';
import { useUserControlsContext } from '../contexts/UserControlsContext';
import { analyticsEnabled, analyticsIdentity } from '../lib/analytics';

/**
 * Returns a Segment tracker that attaches the current user / impersonation
 * identity to every event. Emits in production always; in non-production only
 * when opted in (localStorage `terra_analytics_dev=1`) — see analyticsEnabled.
 * Swallows analytics errors. Build feature-specific hooks (e.g.
 * useTrackAccountViewed, useTrackViewViewed) on top of this rather than calling
 * window.analytics directly, so the identity prop shape stays consistent.
 *
 * Every event carries the catalog identity spine (rep_id / user_type /
 * flow_role / env — matches server events for warehouse joins). The legacy
 * camelCase aliases (userId / userEmail / userType / impersonatorId) are kept
 * for the pre-existing events' dashboards.
 */
export function useSegmentTrack(): (event: string, properties?: Record<string, unknown>) => void {
  const controls = useUserControlsContext();
  const { userId, email: userEmail, impersonation } = controls;
  // Legacy `userType` alias reports the REAL team, never the "UI View"
  // lens, same as the snake_case spine (analyticsIdentity).
  const userType = controls.realUserType;
  const impersonatorId = impersonation?.impersonatorId ?? null;
  const impersonatorEmail = impersonation?.impersonatorEmail ?? null;

  return useCallback((event: string, properties?: Record<string, unknown>) => {
    if (!analyticsEnabled()) return;
    try {
      window.analytics?.track(event, {
        ...properties,
        ...analyticsIdentity(controls),
        // Legacy identity aliases — kept for existing events' dashboards.
        userId,
        userEmail,
        userType,
        impersonatorId,
        impersonatorEmail,
      });
    } catch {}
    // controls object identity changes when any field does; the primitives keep
    // the callback stable across renders where identity is unchanged.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, userEmail, userType, impersonatorId, impersonatorEmail]);
}
